import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Input, Spinner } from '@shared/ui';
import { pickLocalized, useCurrentLocale, type Locale } from '@shared/i18n';
import { taxonomyApi, type Category, type CategoryKind } from '@entities/taxonomy';

interface Row {
  category: Category;
  depth: number;
}

/**
 * Flattens the category tree into indented rows, keeping a node when it — or any
 * of its descendants — matches the search query, so hierarchy stays readable
 * while filtering.
 */
function buildRows(all: Category[], locale: Locale, query: string): Row[] {
  const ids = new Set(all.map((c) => c.id));
  const children = new Map<string | null, Category[]>();
  for (const c of all) {
    const key = c.parentId && ids.has(c.parentId) ? c.parentId : null;
    const bucket = children.get(key) ?? [];
    bucket.push(c);
    children.set(key, bucket);
  }
  for (const bucket of children.values()) {
    bucket.sort((a, b) => a.sortOrder - b.sortOrder || a.slug.localeCompare(b.slug));
  }

  const needle = query.trim().toLowerCase();
  const matches = (c: Category) =>
    !needle ||
    pickLocalized(c.name, locale, c.slug).toLowerCase().includes(needle) ||
    c.slug.toLowerCase().includes(needle);

  const keep = new Set<string>();
  const visit = (c: Category): boolean => {
    let descendantMatched = false;
    for (const child of children.get(c.id) ?? []) {
      if (visit(child)) descendantMatched = true;
    }
    const ok = matches(c) || descendantMatched;
    if (ok) keep.add(c.id);
    return ok;
  };
  for (const root of children.get(null) ?? []) visit(root);

  const rows: Row[] = [];
  const walk = (list: Category[], depth: number) => {
    for (const category of list) {
      if (!keep.has(category.id)) continue;
      rows.push({ category, depth });
      walk(children.get(category.id) ?? [], depth + 1);
    }
  };
  walk(children.get(null) ?? [], 0);
  return rows;
}

export function CategoryPicker({
  kind,
  label,
  hint,
  selected,
  onChange,
}: {
  kind: CategoryKind;
  label: string;
  hint?: string;
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const locale = useCurrentLocale();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['categories', kind],
    queryFn: () => taxonomyApi.listCategories(kind),
  });

  const all = useMemo(() => data ?? [], [data]);
  const byId = useMemo(() => new Map(all.map((c) => [c.id, c])), [all]);
  const rows = useMemo(() => buildRows(all, locale, query), [all, locale, query]);

  // `selected` is a mixed list across all kinds — operate only on our own ids.
  const ownSelected = selected.filter((id) => byId.has(id));

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const clearOwn = () => onChange(selected.filter((id) => !byId.has(id)));

  const nameOf = (id: string) => {
    const c = byId.get(id);
    return c ? pickLocalized(c.name, locale, c.slug) : id.slice(0, 8);
  };

  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-accent/50"
      >
        <span className="min-w-0">
          <span className="text-sm font-medium">{label}</span>
          {hint ? <span className="block text-[11px] text-muted-foreground">{hint}</span> : null}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {ownSelected.length > 0 ? (
            <Badge variant="secondary">{ownSelected.length}</Badge>
          ) : null}
          <span className="text-xs text-muted-foreground">{open ? '▾' : '▸'}</span>
        </span>
      </button>

      {ownSelected.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1 border-t px-3 py-2">
          {ownSelected.map((id) => (
            <span
              key={id}
              className="inline-flex max-w-full items-center gap-1 rounded bg-primary/10 py-0.5 pl-2 pr-1 text-xs text-primary"
            >
              <span className="truncate">{nameOf(id)}</span>
              <button
                type="button"
                onClick={() => toggle(id)}
                className="rounded px-1 leading-none hover:bg-primary/20"
                aria-label="Убрать"
              >
                ✕
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearOwn}
            className="ml-1 text-[11px] text-muted-foreground hover:text-destructive"
          >
            очистить
          </button>
        </div>
      ) : null}

      {open ? (
        <div className="space-y-2 border-t p-2">
          <Input
            placeholder="Поиск категории…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-8 text-sm"
          />

          {isLoading ? (
            <div className="flex justify-center py-4">
              <Spinner className="h-4 w-4" />
            </div>
          ) : rows.length === 0 ? (
            <p className="px-2 py-3 text-center text-xs text-muted-foreground">
              {all.length === 0 ? 'Категорий этого типа нет' : 'Ничего не найдено'}
            </p>
          ) : (
            <div className="max-h-52 overflow-y-auto">
              {rows.map(({ category, depth }) => {
                const checked = selected.includes(category.id);
                return (
                  <label
                    key={category.id}
                    style={{ paddingLeft: `${depth * 16 + 8}px` }}
                    className="flex cursor-pointer items-center gap-2 rounded py-1 pr-2 text-sm hover:bg-accent"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(category.id)}
                      className="h-4 w-4 shrink-0 accent-current"
                    />
                    <span className="truncate">{pickLocalized(category.name, locale, category.slug)}</span>
                    <span className="ml-auto shrink-0 font-mono text-[10px] text-muted-foreground">
                      {category.slug}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
